import { confirmDialog, showDialog } from '@/utils/dialogs';
/**
 * CohortsTab — school-owned student groupings.
 *
 * List active cohorts, create new ones, inspect members, archive, and
 * assign students. A student can only have one active cohort at a time;
 * adding a student who is already active elsewhere surfaces a 409 with
 * the conflicting cohort id so the caller can offer a transfer.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  TextInput,
  Platform,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';

import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useTranslation } from '../../hooks/useTranslation';
import { CohortCsvInviteFile, CohortCsvInviteResponse, cohortsAPI, Cohort, CohortMember } from '../../services/api';
import { colors } from '@/design-system';
import { countPersonalSubscriptionWarnings } from './inviteWarnings';

interface Student {
  id: number;
  email: string;
  full_name?: string;
}

interface CohortsTabProps {
  schoolId: number;
  students: Student[];
  canEdit: boolean;
  onRosterChanged?: () => Promise<void> | void;
}

export const CohortsTab: React.FC<CohortsTabProps> = ({
  schoolId,
  students,
  canEdit,
  onRosterChanged,
}) => {
  const { t } = useTranslation();

  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [membersByCohort, setMembersByCohort] = useState<Record<number, CohortMember[]>>({});
  const [loadingMembersFor, setLoadingMembersFor] = useState<number | null>(null);

  const [addingToCohortId, setAddingToCohortId] = useState<number | null>(null);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [csvUploadingFor, setCsvUploadingFor] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await cohortsAPI.list(schoolId, false);
      setCohorts(list);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load cohorts');
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMembers = useCallback(
    async (cohortId: number) => {
      try {
        setLoadingMembersFor(cohortId);
        const members = await cohortsAPI.listMembers(schoolId, cohortId);
        setMembersByCohort((prev) => ({ ...prev, [cohortId]: members }));
      } catch (e: any) {
        showDialog(
          t('common.error'),
          e?.response?.data?.detail || 'Failed to load members',
        );
      } finally {
        setLoadingMembersFor(null);
      }
    },
    [schoolId, t],
  );

  const toggleExpand = (cohortId: number) => {
    const next = expandedId === cohortId ? null : cohortId;
    setExpandedId(next);
    if (next && !membersByCohort[next]) {
      loadMembers(next);
    }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      setCreating(true);
      await cohortsAPI.create(schoolId, {
        name,
        description: newDesc.trim() || null,
      });
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
      await load();
    } catch (e: any) {
      showDialog(
        t('common.error'),
        e?.response?.data?.detail || 'Failed to create cohort',
      );
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = async (cohort: Cohort) => {
    const archive = async () => {
      try {
        await cohortsAPI.archive(schoolId, cohort.id);
        await load();
      } catch (e: any) {
        showDialog(
          t('common.error'),
          e?.response?.data?.detail || 'Failed to archive cohort',
        );
      }
    };
    const confirmed = await confirmDialog(
      t('cohorts.archiveTitle'),
      t('cohorts.archiveConfirm'),
      t('cohorts.archive'),
      t('common.cancel'),
    );
    if (confirmed) await archive();
  };

  const handleRemoveMember = async (cohortId: number, userId: number) => {
    try {
      await cohortsAPI.removeMember(schoolId, cohortId, userId);
      await loadMembers(cohortId);
      await load();
    } catch (e: any) {
      showDialog(
        t('common.error'),
        e?.response?.data?.detail || 'Failed to remove member',
      );
    }
  };

  const handleAddStudent = async (cohortId: number, userId: number, allowTransfer = false) => {
    try {
      setAddSubmitting(true);
      if (allowTransfer) {
        await cohortsAPI.transfer(schoolId, cohortId, userId);
      } else {
        await cohortsAPI.addMembers(schoolId, cohortId, [userId]);
      }
      await loadMembers(cohortId);
      await load();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      // 409 with conflicting_user_ids -> offer transfer
      const isConflict =
        e?.response?.status === 409 &&
        detail &&
        typeof detail === 'object' &&
        Array.isArray(detail.conflicting_user_ids);
      if (isConflict) {
        const confirmed = await confirmDialog(
          t('cohorts.transferTitle'),
          t(
            'cohorts.transferPrompt',
          ),
          t('cohorts.transfer'),
          t('common.cancel'),
        );
        if (confirmed) await handleAddStudent(cohortId, userId, true);
      } else {
        showDialog(
          t('common.error'),
          (typeof detail === 'string' ? detail : null) || 'Failed to add student',
        );
      }
    } finally {
      setAddSubmitting(false);
    }
  };

  const csvSummaryMessage = (result: CohortCsvInviteResponse) => {
    const base = t(
      'cohorts.csvInviteSummary',
      {
        invited: result.invited_count,
        added: result.added_existing_count,
      },
    );
    const skipped = result.skipped_count
      ? `\n${t('cohorts.csvInviteSkipped', { count: result.skipped_count })}`
      : '';
    const emailFailures = result.invitation_email_failed_count
      ? `\n${t('cohorts.csvInviteEmailFailures', { count: result.invitation_email_failed_count })}`
      : '';
    const subscriptionWarnings = countPersonalSubscriptionWarnings(result.results);
    const subscriptionWarning = subscriptionWarnings
      ? `\n${t('cohorts.csvInvitePersonalSubscriptionWarning', { count: subscriptionWarnings })}`
      : '';
    const failures = result.results
      .filter((row) => row.status === 'failed')
      .slice(0, 4)
      .map((row) => `Row ${row.row}: ${row.email || '-'} — ${row.reason || 'Failed'}`)
      .join('\n');
    const failureBlock = result.failed_count
      ? `\n${t('cohorts.csvInviteFailed', { count: result.failed_count })}${failures ? `\n${failures}` : ''}`
      : '';
    return `${base}${skipped}${emailFailures}${subscriptionWarning}${failureBlock}`;
  };

  const uploadCsvInviteFile = async (cohortId: number, file: CohortCsvInviteFile) => {
    try {
      setCsvUploadingFor(cohortId);
      const result = await cohortsAPI.inviteCsv(schoolId, cohortId, file, true);
      await loadMembers(cohortId);
      await load();
      await onRosterChanged?.();

      showDialog(t('cohorts.csvInviteComplete'), csvSummaryMessage(result));
    } catch (e: any) {
      showDialog(
        t('common.error'),
        e?.message || t('cohorts.csvInviteFailedGeneric'),
      );
    } finally {
      setCsvUploadingFor(null);
    }
  };

  const handleCsvInvite = async (cohortId: number) => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.csv,text/csv';
      input.onchange = async (event: Event) => {
        const target = event.target as HTMLInputElement | null;
        const file = target?.files?.[0];
        if (file) {
          await uploadCsvInviteFile(cohortId, file);
        }
      };
      input.click();
      return;
    }

    const result = await DocumentPicker.getDocumentAsync({
      type: ['text/csv', 'text/comma-separated-values'],
      copyToCacheDirectory: true,
      multiple: false,
    });

    if (result.canceled || !result.assets?.[0]) {
      return;
    }

    const asset = result.assets[0];
    await uploadCsvInviteFile(cohortId, {
      uri: asset.uri,
      name: asset.name || 'cohort-invites.csv',
      type: asset.mimeType || 'text/csv',
    });
  };

  // Students that can be added to a cohort = school students not currently
  // in the members list of THIS cohort. Backend still validates.
  const pickableStudents = (cohortId: number) => {
    const current = new Set((membersByCohort[cohortId] || []).map((m) => m.user_id));
    return students.filter((s) => !current.has(s.id));
  };

  if (loading) {
    return (
      <View className="mb-6">
        <Card>
          <CardContent className="p-8 items-center">
            <ActivityIndicator size="large" color={colors.primary} />
            <Text className="text-base text-muted-foreground mt-4">
              {t('cohorts.loading')}
            </Text>
          </CardContent>
        </Card>
      </View>
    );
  }

  if (error) {
    return (
      <View className="mb-6">
        <Card>
          <CardContent className="p-6 items-center">
            <MaterialIcons name="error-outline" size={40} color="#DC2626" />
            <Text className="text-base text-foreground mt-3 mb-3">{error}</Text>
            <Button variant="outline" size="sm" onPress={load}>
              <Text>{t('common.retry')}</Text>
            </Button>
          </CardContent>
        </Card>
      </View>
    );
  }

  return (
    <View className="mb-6">
      <View className="flex-row justify-between items-center mb-4">
        <View>
          <Text className="text-xl font-bold text-foreground">
            {t('cohorts.title')}
          </Text>
          <Text className="text-sm text-muted-foreground mt-0.5">
            {t('cohorts.subtitle')}
          </Text>
        </View>
        {canEdit && (
          <Button variant="default" size="sm" onPress={() => setShowCreate(true)}>
            <MaterialIcons name="add" size={16} color="white" />
            <Text className="text-white text-xs ml-1">
              {t('cohorts.create')}
            </Text>
          </Button>
        )}
      </View>

      {cohorts.length === 0 ? (
        <Card>
          <CardContent className="p-8 items-center">
            <MaterialIcons name="groups" size={48} color="#6b7280" />
            <Text className="text-lg font-medium text-foreground mt-4 mb-2">
              {t('cohorts.emptyTitle')}
            </Text>
            <Text className="text-sm text-muted-foreground text-center mb-4">
              {t(
                'cohorts.emptyDesc',
              )}
            </Text>
            {canEdit && (
              <Button variant="default" onPress={() => setShowCreate(true)}>
                <Text className="text-white font-medium">
                  {t('cohorts.createFirst')}
                </Text>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <View className="gap-3">
          {cohorts.map((cohort) => {
            const isExpanded = expandedId === cohort.id;
            const members = membersByCohort[cohort.id] || [];
            return (
              <Card key={cohort.id}>
                <CardContent className="p-4">
                  <TouchableOpacity
                    onPress={() => toggleExpand(cohort.id)}
                    activeOpacity={0.7}
                  >
                    <View className="flex-row items-start justify-between mb-1">
                      <View className="flex-1">
                        <Text className="text-base font-semibold text-foreground">
                          {cohort.name}
                        </Text>
                        {cohort.description ? (
                          <Text className="text-sm text-muted-foreground mt-0.5">
                            {cohort.description}
                          </Text>
                        ) : null}
                      </View>
                      <View className="bg-blue-50 px-3 py-1 rounded-full">
                        <Text className="text-xs font-semibold text-blue-700">
                          {t('cohorts.memberCount', {
                            count: cohort.member_count,
                            defaultValue: `${cohort.member_count} member${cohort.member_count === 1 ? '' : 's'}`,
                          })}
                        </Text>
                      </View>
                    </View>
                    <View className="flex-row gap-4 mt-2">
                      {cohort.lead_admin_name ? (
                        <View className="flex-row items-center">
                          <MaterialIcons name="person-outline" size={14} color="#6b7280" />
                          <Text className="text-xs text-muted-foreground ml-1">
                            {cohort.lead_admin_name}
                          </Text>
                        </View>
                      ) : null}
                      {cohort.started_at || cohort.ends_at ? (
                        <View className="flex-row items-center">
                          <MaterialIcons name="event" size={14} color="#6b7280" />
                          <Text className="text-xs text-muted-foreground ml-1">
                            {cohort.started_at || '…'} → {cohort.ends_at || t('cohorts.openEnded')}
                          </Text>
                        </View>
                      ) : null}
                    </View>
                  </TouchableOpacity>

                  {isExpanded && (
                    <View className="mt-4 pt-4 border-t border-gray-200">
                      <View className="flex-row items-center justify-between mb-2">
                        <Text className="text-sm font-semibold text-foreground">
                          {t('cohorts.members')}
                        </Text>
                        {canEdit && (
                          <View className="flex-row gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onPress={() => handleCsvInvite(cohort.id)}
                              disabled={csvUploadingFor === cohort.id}
                            >
                              {csvUploadingFor === cohort.id ? (
                                <ActivityIndicator size="small" color="#1F2937" />
                              ) : (
                                <MaterialIcons name="upload-file" size={14} color="#1F2937" />
                              )}
                              <Text className="text-xs ml-1">
                                {t('cohorts.csvInvite')}
                              </Text>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onPress={() => setAddingToCohortId(cohort.id)}
                            >
                              <MaterialIcons name="person-add" size={14} color="#1F2937" />
                              <Text className="text-xs ml-1">
                                {t('cohorts.addStudents')}
                              </Text>
                            </Button>
                          </View>
                        )}
                      </View>

                      {loadingMembersFor === cohort.id ? (
                        <ActivityIndicator color={colors.primary} />
                      ) : members.length === 0 ? (
                        <Text className="text-xs text-muted-foreground italic py-2">
                          {t('cohorts.noMembers')}
                        </Text>
                      ) : (
                        <View className="gap-2">
                          {members.map((m) => (
                            <View
                              key={m.user_id}
                              className="flex-row items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                            >
                              <View className="flex-1">
                                <Text className="text-sm font-medium text-foreground">
                                  {m.full_name || m.email}
                                </Text>
                                <Text className="text-xs text-muted-foreground">
                                  {m.email}
                                </Text>
                              </View>
                              {canEdit && (
                                <TouchableOpacity
                                  onPress={() => handleRemoveMember(cohort.id, m.user_id)}
                                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                >
                                  <MaterialIcons name="close" size={18} color="#6b7280" />
                                </TouchableOpacity>
                              )}
                            </View>
                          ))}
                        </View>
                      )}

                      {canEdit && (
                        <View className="flex-row justify-end mt-4">
                          <TouchableOpacity
                            onPress={() => handleArchive(cohort)}
                            className="flex-row items-center px-3 py-2"
                          >
                            <MaterialIcons name="archive" size={14} color="#DC2626" />
                            <Text className="text-xs text-red-600 ml-1">
                              {t('cohorts.archive')}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      )}
                    </View>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </View>
      )}

      {/* Create modal */}
      <Modal
        visible={showCreate}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCreate(false)}
      >
        <View className="flex-1 bg-black/50 justify-center items-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <Text className="text-lg font-bold text-foreground">
                {t('cohorts.create')}
              </Text>
            </CardHeader>
            <CardContent>
              <Text className="text-sm font-medium text-foreground mb-1">
                {t('cohorts.nameLabel')}
              </Text>
              <TextInput
                value={newName}
                onChangeText={setNewName}
                placeholder={t('cohorts.namePlaceholder')}
                className="border border-gray-300 rounded-lg px-3 py-2 mb-3 text-foreground"
                maxLength={120}
                autoFocus
              />
              <Text className="text-sm font-medium text-foreground mb-1">
                {t('cohorts.descriptionLabel')}
              </Text>
              <TextInput
                value={newDesc}
                onChangeText={setNewDesc}
                placeholder={t('cohorts.descriptionPlaceholder')}
                className="border border-gray-300 rounded-lg px-3 py-2 text-foreground"
                multiline
                numberOfLines={2}
              />
              <View className="flex-row justify-end gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => setShowCreate(false)}
                  disabled={creating}
                >
                  <Text>{t('common.cancel')}</Text>
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onPress={handleCreate}
                  disabled={creating || !newName.trim()}
                >
                  {creating ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <Text className="text-white">{t('common.create')}</Text>
                  )}
                </Button>
              </View>
            </CardContent>
          </Card>
        </View>
      </Modal>

      {/* Add-students modal */}
      <Modal
        visible={addingToCohortId !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setAddingToCohortId(null)}
      >
        <View className="flex-1 bg-black/50 justify-center items-center p-4">
          <Card className="w-full max-w-md" style={{ maxHeight: '80%' }}>
            <CardHeader>
              <Text className="text-lg font-bold text-foreground">
                {t('cohorts.addStudentsTitle')}
              </Text>
            </CardHeader>
            <CardContent>
              {addingToCohortId !== null && pickableStudents(addingToCohortId).length === 0 ? (
                <Text className="text-sm text-muted-foreground py-4 text-center">
                  {t('cohorts.noStudentsToAdd')}
                </Text>
              ) : (
                <ScrollView style={{ maxHeight: 380 }}>
                  <View className="gap-2">
                    {addingToCohortId !== null &&
                      pickableStudents(addingToCohortId).map((s) => (
                        <TouchableOpacity
                          key={s.id}
                          disabled={addSubmitting}
                          onPress={() => addingToCohortId !== null && handleAddStudent(addingToCohortId, s.id)}
                          className="flex-row items-center justify-between bg-gray-50 rounded-lg px-3 py-3"
                        >
                          <View className="flex-1">
                            <Text className="text-sm font-medium text-foreground">
                              {s.full_name || s.email}
                            </Text>
                            <Text className="text-xs text-muted-foreground">
                              {s.email}
                            </Text>
                          </View>
                          <MaterialIcons name="add-circle-outline" size={20} color={colors.primary} />
                        </TouchableOpacity>
                      ))}
                  </View>
                </ScrollView>
              )}
              <View className="flex-row justify-end mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => setAddingToCohortId(null)}
                >
                  <Text>{t('common.done')}</Text>
                </Button>
              </View>
            </CardContent>
          </Card>
        </View>
      </Modal>
    </View>
  );
};

export default CohortsTab;
